import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useParams, useSearchParams} from 'react-router-dom';

import {confirmPayment} from '../api/api';
import {useCart} from '../context/CartContext';

const PENDING_PAYMENT_KEY = 'pendingPaymentData';
const ORDERS_KEY = 'orders';
const MAX_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 3000;
const MAIN_APP_RETURN_PATH = '/home';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const getStoredPendingPayment = () => {
  try {
    const raw = localStorage.getItem(PENDING_PAYMENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('Failed to read pending payment:', error);
    return null;
  }
};

const upsertLocalOrder = updatedOrder => {
  if (!updatedOrder?.id) return;

  try {
    const existingOrders = JSON.parse(localStorage.getItem(ORDERS_KEY) || '[]');

    const filtered = existingOrders.filter(
      order => String(order.id) !== String(updatedOrder.id),
    );

    localStorage.setItem(
      ORDERS_KEY,
      JSON.stringify([updatedOrder, ...filtered]),
    );
  } catch (error) {
    console.error('Failed to update local orders:', error);
  }
};

const readStatusFromResponse = response => {
  const data = response?.data || response || {};

  return (
    data?.data?.paymentStatus ||
    data?.data?.payment_status ||
    data?.data?.status ||
    data?.paymentStatus ||
    data?.payment_status ||
    data?.status ||
    data?.order?.paymentStatus ||
    data?.order?.payment_status ||
    data?.order?.status ||
    data?.ikhokhaStatus ||
    ''
  )
    .toString()
    .toLowerCase();
};

export default function PaymentReturn() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const {clearCart} = useCart();

  const result = params.result === 'cancel' ? 'cancel' : 'success';

  const [phase, setPhase] = useState(
    result === 'cancel' ? 'cancelled' : 'confirming',
  );
  const [message, setMessage] = useState('');

  const pendingPayment = useMemo(() => getStoredPendingPayment(), []);

  const openMainApp = useCallback((path = MAIN_APP_RETURN_PATH) => {
    window.location.replace(path);
  }, []);

  const orderId =
    searchParams.get('orderId') ||
    searchParams.get('order_id') ||
    searchParams.get('reference') ||
    searchParams.get('orderReference') ||
    pendingPayment?.serverOrder?.id ||
    pendingPayment?.serverOrder?._id ||
    pendingPayment?.order?.id ||
    pendingPayment?.order?._id;

  const paymentReference =
    searchParams.get('reference') ||
    searchParams.get('transactionId') ||
    searchParams.get('transaction_id') ||
    searchParams.get('paymentReference') ||
    searchParams.get('payment_reference') ||
    pendingPayment?.payment?.reference ||
    pendingPayment?.payment?.transactionId ||
    pendingPayment?.serverOrder?.ikhokhaReference ||
    pendingPayment?.serverOrder?.paymentReference;
  const returnToMobile = searchParams.get('returnTo') === 'mobile';

  const finaliseOrder = useCallback(
    paymentStatus => {
      const currentOrder = pendingPayment?.order;

      if (currentOrder) {
        upsertLocalOrder({
          ...currentOrder,
          paymentStatus,
          paymentConfirmed: paymentStatus === 'paid',
          status:
            paymentStatus === 'paid'
              ? currentOrder.status || 'pending'
              : currentOrder.status || 'payment pending',
          updatedAt: new Date().toISOString(),
        });
      }

      if (paymentStatus === 'paid') {
        clearCart();
        localStorage.removeItem(PENDING_PAYMENT_KEY);
      }
    },
    [clearCart, pendingPayment],
  );

  useEffect(() => {
    let cancelled = false;
    let redirectTimer = null;

    const confirm = async () => {
      if (returnToMobile) {
        if (!orderId) {
          setPhase('error');
          setMessage(
            'The payment return did not include an order number. Return to the app and check your orders.',
          );
          return;
        }

        setPhase('returning');
        window.location.replace(
          `yabatho://payment/success?orderId=${encodeURIComponent(orderId)}`,
        );
        return;
      }

      if (result === 'cancel') {
        finaliseOrder('cancelled');
        return;
      }

      if (!orderId) {
        setPhase('error');
        setMessage(
          'We could not identify the order for this payment. Please check your orders before trying again.',
        );
        return;
      }

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
          const response = await confirmPayment(orderId, {
            reference: paymentReference,
            transactionId: paymentReference,
          });
          const status = readStatusFromResponse(response);

          if (['success', 'paid', 'confirmed', 'completed'].includes(status)) {
            if (!cancelled) {
              finaliseOrder('paid');
              setPhase('success');

              redirectTimer = setTimeout(() => {
                openMainApp();
              }, 300);
            }

            return;
          }

          if (['failed', 'cancelled', 'canceled', 'declined'].includes(status)) {
            if (!cancelled) {
              finaliseOrder('failed');
              setPhase('error');
              setMessage(
                'Payment was not successful. You can retry from your orders once retry payment is available.',
              );
            }

            return;
          }
        } catch (error) {
          if (attempt === MAX_ATTEMPTS && !cancelled) {
            setPhase('error');
            setMessage(
              error?.message ||
                'Unable to confirm payment. Please check your order status.',
            );
            return;
          }
        }

        if (attempt < MAX_ATTEMPTS) {
          await sleep(POLL_INTERVAL_MS);
        }
      }

      if (!cancelled) {
        setPhase('error');
        setMessage('Payment is still processing. Please check your orders shortly.');
      }
    };

    confirm();

    return () => {
      cancelled = true;

      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [
    finaliseOrder,
    openMainApp,
    orderId,
    paymentReference,
    result,
    returnToMobile,
  ]);

  const content = {
    confirming: {
      icon: '⏳',
      title: 'Confirming your payment',
      body: 'We are checking iKhokha and updating your YaBatho order.',
    },
    returning: {
      icon: '↗',
      title: 'Returning to YaBatho',
      body: 'Your bank OTP was submitted. Open the YaBatho app to complete payment confirmation.',
    },
    success: {
      icon: '✅',
      title: 'Payment confirmed',
      body: 'Your order has been received. Taking you back to the app now.',
    },
    cancelled: {
      icon: '⚠️',
      title: 'Payment not completed',
      body: 'You left the payment page before completing payment. Your order was saved as pending.',
    },
    error: {
      icon: '⚠️',
      title: 'Payment needs checking',
      body:
        message ||
        'We could not confirm the payment automatically. Check your orders for the latest status.',
    },
  }[phase];

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.card)}>
        <div className={css(styles.icon)}>{content.icon}</div>

        <p className={css(styles.kicker)}>iKhokha payment</p>

        <h1 className={css(styles.title)}>{content.title}</h1>

        <p className={css(styles.body)}>{content.body}</p>

        <div className={css(styles.actions)}>
          <button
            type="button"
            className={css(styles.primaryButton)}
            onClick={() => {
              if (returnToMobile && orderId) {
                window.location.href = `yabatho://payment/success?orderId=${encodeURIComponent(orderId)}`;
                return;
              }

              openMainApp('/orders');
            }}>
            {returnToMobile ? 'Open YaBatho App' : 'View my orders'}
          </button>

          <button
            type="button"
            className={css(styles.secondaryButton)}
            onClick={() => openMainApp()}>
            Go home
          </button>
        </div>
      </main>
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '520px',
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '34px',
    padding: '34px',
    textAlign: 'center',
    boxShadow: '0 22px 54px rgba(15, 61, 46, 0.14)',
  },
  icon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    margin: '0 auto 18px',
    backgroundColor: '#F4EBD8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '34px',
  },
  kicker: {
    margin: '0 0 8px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  title: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '34px',
    letterSpacing: '-1px',
    lineHeight: 1.08,
  },
  body: {
    margin: '14px auto 0',
    maxWidth: '420px',
    color: '#6E6254',
    fontSize: '15px',
    lineHeight: 1.75,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '28px',
  },
  primaryButton: {
    border: 'none',
    backgroundColor: '#0F3D2E',
    color: '#C9A14A',
    borderRadius: '18px',
    padding: '14px 20px',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },
  secondaryButton: {
    border: '1px solid #E6D8BE',
    backgroundColor: '#ffffff',
    color: '#0F3D2E',
    borderRadius: '18px',
    padding: '14px 20px',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },
});
